import type { ImportedPageDocument } from "./import-types";
import { parseAttributes } from "./document-parser";
import type {
  StudioContentBlock,
  StudioPageContent,
  StudioPageSection,
} from "../model/page-content";

type HtmlToken =
  | {
      type: "start";
      tagName: string;
      attributes: Record<string, string>;
      selfClosing: boolean;
    }
  | {
      type: "end";
      tagName: string;
    }
  | {
      type: "text";
      value: string;
    };

type OpenElement = {
  tagName: string;
  attributes: Record<string, string>;
  textParts: string[];
};

type SectionAccumulator = {
  id: string;
  blocks: StudioContentBlock[];
};

const HEADING_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
]);

const IGNORED_TAGS = new Set([
  "script",
  "style",
  "noscript",
  "template",
  "nav",
  "header",
  "footer",
]);

const VOID_TAGS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

function decodeHtmlText(value: string): string {
  return value
    .replace(/&nbsp;/gi, " ")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#(\d+);/g, (_, decimal: string) => {
      const codePoint = Number.parseInt(decimal, 10);

      return Number.isFinite(codePoint)
        ? String.fromCodePoint(codePoint)
        : "";
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, hexadecimal: string) => {
      const codePoint = Number.parseInt(hexadecimal, 16);

      return Number.isFinite(codePoint)
        ? String.fromCodePoint(codePoint)
        : "";
    });
}

function normalizeText(value: string | null | undefined): string {
  return decodeHtmlText(value ?? "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:!?])/g, "$1")
    .trim();
}

function tokenizeHtml(source: string): HtmlToken[] {
  const tokens: HtmlToken[] = [];
  const pattern =
    /<!--[\s\S]*?-->|<![^>]*>|<\/?([a-zA-Z][\w:-]*)([^>]*)>|([^<]+)/g;

  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source))) {
    const wholeMatch = match[0] ?? "";

    if (
      wholeMatch.startsWith("<!--") ||
      wholeMatch.startsWith("<!")
    ) {
      continue;
    }

    const tagName = (match[1] ?? "").toLowerCase();

    if (tagName) {
      if (wholeMatch.startsWith("</")) {
        tokens.push({
          type: "end",
          tagName,
        });

        continue;
      }

      const rawAttributes = match[2] ?? "";
      const selfClosing =
        /\/\s*>$/.test(wholeMatch) ||
        VOID_TAGS.has(tagName);

      tokens.push({
        type: "start",
        tagName,
        attributes: parseAttributes(
          rawAttributes.replace(/\/\s*$/, ""),
        ),
        selfClosing,
      });

      continue;
    }

    const text = match[3] ?? "";

    if (text) {
      tokens.push({
        type: "text",
        value: text,
      });
    }
  }

  return tokens;
}

function sectionHeading(
  blocks: StudioContentBlock[],
): string | undefined {
  return blocks.find(
    (block) => block.type === "heading",
  )?.text;
}

function finalizeSection(
  section: SectionAccumulator,
): StudioPageSection | null {
  if (!section.blocks.length) {
    return null;
  }

  return {
    id: section.id,
    heading: sectionHeading(section.blocks),
    blocks: section.blocks,
  };
}

function createSectionAccumulator(
  index: number,
): SectionAccumulator {
  return {
    id: `section-${index}`,
    blocks: [],
  };
}

function isInsideTag(
  stack: OpenElement[],
  tagNames: Set<string>,
): boolean {
  return stack.some((element) =>
    tagNames.has(element.tagName),
  );
}

function isInsideIgnoredContent(
  stack: OpenElement[],
): boolean {
  return isInsideTag(stack, IGNORED_TAGS);
}

function nearestOpenElement(
  stack: OpenElement[],
  predicate: (element: OpenElement) => boolean,
): OpenElement | undefined {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const element = stack[index];

    if (element && predicate(element)) {
      return element;
    }
  }

  return undefined;
}

function elementText(element: OpenElement): string {
  return normalizeText(element.textParts.join(" "));
}

function appendText(
  stack: OpenElement[],
  value: string,
): void {
  if (isInsideIgnoredContent(stack)) {
    return;
  }

  stack.forEach((element) => {
    element.textParts.push(value);
  });
}

function extractClosedBlock(
  element: OpenElement,
  stack: OpenElement[],
): StudioContentBlock | null {
  const text = elementText(element);

  if (HEADING_TAGS.has(element.tagName)) {
    if (!text) {
      return null;
    }

    return {
      type: "heading",
      level: Number(
        element.tagName.slice(1),
      ) as 1 | 2 | 3 | 4 | 5 | 6,
      text,
    };
  }

  if (element.tagName === "p") {
    if (!text) {
      return null;
    }

    return {
      type: "paragraph",
      text,
    };
  }

  if (element.tagName === "blockquote") {
    if (!text) {
      return null;
    }

    return {
      type: "quote",
      text,
    };
  }

  if (element.tagName === "a") {
    const href = normalizeText(element.attributes.href);

    if (!href || !text) {
      return null;
    }

    const contentParent = nearestOpenElement(
      stack,
      (candidate) =>
        candidate.tagName === "p" ||
        candidate.tagName === "li" ||
        candidate.tagName === "blockquote" ||
        HEADING_TAGS.has(candidate.tagName),
    );

    if (contentParent) {
      return null;
    }

    return {
      type: "link",
      link: {
        href,
        text,
      },
    };
  }

  return null;
}

function extractImageBlock(
  attributes: Record<string, string>,
): StudioContentBlock | null {
  const src = normalizeText(
    attributes.src || attributes["data-src"],
  );

  if (!src) {
    return null;
  }

  const alt = normalizeText(attributes.alt);

  return {
    type: "image",
    image: {
      src,
      alt: alt || undefined,
    },
  };
}

function shouldUseMainContent(tokens: HtmlToken[]): boolean {
  return tokens.some(
    (token) =>
      token.type === "start" &&
      token.tagName === "main",
  );
}

export function extractPageContent(
  page: ImportedPageDocument,
): StudioPageContent {
  const tokens = tokenizeHtml(page.bodyHtml);
  const useMainContent = shouldUseMainContent(tokens);

  const stack: OpenElement[] = [];
  const sections: StudioPageSection[] = [];

  let mainDepth = 0;
  let explicitSectionDepth = 0;
  let activeSection: SectionAccumulator | null =
    createSectionAccumulator(1);

  const listStack: Array<{
    ordered: boolean;
    items: string[];
  }> = [];

  const appendBlock = (
    block: StudioContentBlock | null,
  ): void => {
    if (!block) {
      return;
    }

    if (!activeSection) {
      activeSection = createSectionAccumulator(
        sections.length + 1,
      );
    }

    activeSection.blocks.push(block);
  };

  const flushActiveSection = (): void => {
    if (!activeSection) {
      return;
    }

    const finalized = finalizeSection(activeSection);

    if (finalized) {
      sections.push(finalized);
    }

    activeSection = null;
  };

  for (const token of tokens) {
    if (token.type === "text") {
      if (
        !useMainContent ||
        mainDepth > 0
      ) {
        appendText(stack, token.value);
      }

      continue;
    }

    if (token.type === "start") {
      const insideContent =
        !useMainContent ||
        mainDepth > 0 ||
        token.tagName === "main";

      if (token.tagName === "main") {
        mainDepth += 1;
      }

      const openElement: OpenElement = {
        tagName: token.tagName,
        attributes: token.attributes,
        textParts: [],
      };

      if (!token.selfClosing) {
        stack.push(openElement);
      }

      if (!insideContent) {
        continue;
      }

      if (
        token.tagName === "section" &&
        !isInsideIgnoredContent(stack)
      ) {
        explicitSectionDepth += 1;

        if (explicitSectionDepth === 1) {
          flushActiveSection();

          activeSection = createSectionAccumulator(
            sections.length + 1,
          );
        }
      }

      if (
        token.tagName === "ul" ||
        token.tagName === "ol"
      ) {
        listStack.push({
          ordered: token.tagName === "ol",
          items: [],
        });
      }

      if (token.tagName === "img") {
        appendBlock(
          extractImageBlock(token.attributes),
        );
      }

      continue;
    }

    const closingIndex = (() => {
      for (
        let index = stack.length - 1;
        index >= 0;
        index -= 1
      ) {
        if (stack[index]?.tagName === token.tagName) {
          return index;
        }
      }

      return -1;
    })();

    if (closingIndex < 0) {
      if (token.tagName === "main" && mainDepth > 0) {
        mainDepth -= 1;
      }

      continue;
    }

    const element = stack[closingIndex];

    if (!element) {
      continue;
    }

    stack.splice(closingIndex, 1);

    const insideContent =
      !useMainContent ||
      mainDepth > 0 ||
      token.tagName === "main";

    if (
      insideContent &&
      !IGNORED_TAGS.has(element.tagName) &&
      !isInsideIgnoredContent(stack)
    ) {
      if (element.tagName === "li") {
        const list = listStack[listStack.length - 1];
        const text = elementText(element);

        if (list && text) {
          list.items.push(text);
        }
      } else if (
        element.tagName === "ul" ||
        element.tagName === "ol"
      ) {
        const list = listStack.pop();

        if (list?.items.length) {
          appendBlock({
            type: "list",
            ordered: list.ordered,
            items: list.items,
          });
        }
      } else {
        appendBlock(
          extractClosedBlock(element, stack),
        );
      }
    }

    if (
      token.tagName === "section" &&
      explicitSectionDepth > 0
    ) {
      if (explicitSectionDepth === 1) {
        flushActiveSection();
      }

      explicitSectionDepth -= 1;
    }

    if (token.tagName === "main" && mainDepth > 0) {
      mainDepth -= 1;
    }
  }

  flushActiveSection();

  return {
    sourceRoute: page.route,
    sourceTitle: page.title,
    sections,
  };
}
